import React, { useState, useEffect } from "react";
import { db, ref, push, onValue, remove } from "./firebase";
import { formatCourseId } from "./utils/formatCourse";

const AdminPastPapers = () => {
  const [courses, setCourses] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [year, setYear] = useState("");
  const [session, setSession] = useState("Jan");
  const [folder, setFolder] = useState("QP");
  const [paperName, setPaperName] = useState("");
  const [paperUrl, setPaperUrl] = useState("");
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isAddPaperExpanded, setIsAddPaperExpanded] = useState(false);

  // Fetch all courses and their past papers
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        setCourses(snapshot.val());
      } else {
        setCourses({});
      }
    });
  }, []);

  const courseIds = Object.keys(courses);

  const handleCourseSelection = (courseId) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const handleSelectAllCourses = () => {
    if (selectedCourses.length === courseIds.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses([...courseIds]);
    }
  };

  // Add a new past paper
  const handleAddPaper = async () => {
    if (!year || !session || !folder || !paperName || !paperUrl) return;

    const paperData = { name: paperName.trim(), file: paperUrl.trim() };

    if (isBulkMode) {
      if (selectedCourses.length === 0) {
        alert("Please select at least one course");
        return;
      }
      try {
        const promises = selectedCourses.map((courseId) => {
          const paperRef = ref(db, `courses/${courseId}/pastpapers/${year}/${session}/${folder}`);
          return push(paperRef, paperData);
        });
        await Promise.all(promises);
        alert(`Past paper added successfully to ${selectedCourses.length} course(s)`);
        setPaperName("");
        setPaperUrl("");
        setSelectedCourses([]);
      } catch (error) {
        alert("Error adding past paper to some courses: " + error.message);
      }
    } else {
      if (!selectedCourse) {
        alert("Please select a course");
        return;
      }
      const paperRef = ref(db, `courses/${selectedCourse}/pastpapers/${year}/${session}/${folder}`);
      push(paperRef, paperData)
        .then(() => {
          setPaperName("");
          setPaperUrl("");
        })
        .catch((error) => alert("Error adding past paper: " + error.message));
    }
  };

  // Delete a past paper
  const handleDeletePaper = (course, year, session, folder, paperKey) => {
    const paperRef = ref(db, `courses/${course}/pastpapers/${year}/${session}/${folder}/${paperKey}`);
    remove(paperRef);
  };

  // Toggle year visibility
  const handleYearToggle = (year) => {
    setSelectedYear((prev) => (prev === year ? null : year));
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-black mb-6">Past Papers</h1>

      {/* Course selector */}
      <div className="mb-4">
        <label htmlFor="pastpapers-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          id="pastpapers-course"
          value={selectedCourse ?? ""}
          onChange={(e) => {
            setSelectedCourse(e.target.value || null);
            setSelectedYear(null);
          }}
          className="p-2 border rounded-lg text-black min-w-[200px]"
        >
          <option value="">Select course</option>
          {courseIds.map((course) => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {selectedCourse && (
        <>
      {/* Add New Past Paper button */}
      <div className="flex justify-end mb-6">
        <button
          type="button"
          onClick={() => setIsAddPaperExpanded((prev) => !prev)}
          className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700"
        >
          {isAddPaperExpanded ? "Cancel" : "Add New Past Paper"}
        </button>
      </div>

      {/* Add New Past Paper Form */}
      {isAddPaperExpanded && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6 w-[90%] sm:w-[80%] mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Add New Past Paper</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Single Course</span>
              <button
              type="button"
              onClick={() => {
                setIsBulkMode(!isBulkMode);
                if (!isBulkMode) setSelectedCourses([]);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isBulkMode ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isBulkMode ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">Bulk Upload</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {!isBulkMode && (
            <select
              value={selectedCourse ?? ""}
              onChange={(e) => setSelectedCourse(e.target.value || null)}
              className="border p-2 rounded-lg text-black"
            >
              <option value="">Select Course</option>
              {courseIds.map((course) => (
                <option key={course} value={course}>
                  {formatCourseId(course)}
                </option>
              ))}
            </select>
          )}
          <input
            type="number"
            placeholder="Year (e.g. 2024)"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border p-2 rounded-lg text-black"
          />
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="border p-2 rounded-lg text-black"
          >
            <option value="Jan">January</option>
            <option value="March">March</option>
            <option value="June">June</option>
            <option value="Nov">November</option>
          </select>
          <select
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            className="border p-2 rounded-lg text-black"
          >
            <option value="QP">Question Papers</option>
            <option value="MS">Mark Schemes</option>
          </select>
          <input
            type="text"
            placeholder="Paper Name"
            value={paperName}
            onChange={(e) => setPaperName(e.target.value)}
            className="border p-2 rounded-lg text-black"
          />
          <input
            type="text"
            placeholder="Paper URL"
            value={paperUrl}
            onChange={(e) => setPaperUrl(e.target.value)}
            className="border p-2 rounded-lg text-black"
          />
        </div>

        {isBulkMode && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Select Courses ({selectedCourses.length} selected)
              </label>
              <button
                type="button"
                onClick={handleSelectAllCourses}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {selectedCourses.length === courseIds.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {courseIds.map((course) => (
                <label
                  key={course}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-200 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course)}
                    onChange={() => handleCourseSelection(course)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">{formatCourseId(course)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {!isBulkMode && selectedCourse && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Course:</span> {formatCourseId(selectedCourse)}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={handleAddPaper}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          {isBulkMode ? `Add to ${selectedCourses.length} Course(s)` : "Add Past Paper"}
        </button>
        </div>
      )}
        </>
      )}

      {/* Past Papers List */}
      {!selectedCourse ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm w-[90%] sm:w-[80%] mx-auto">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
          <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
        </div>
      ) : (
        <div className="bg-white p-6 shadow-lg rounded-xl border border-gray-200 w-[90%] sm:w-[80%] mx-auto">
          {Object.keys(courses[selectedCourse].pastpapers || {})
            .sort((a, b) => b - a) // Sort years in descending order
            .map((year) => (
              <div key={year} className="mb-4">
                <button
                  onClick={() => handleYearToggle(year)}
                  className="w-full text-left text-lg font-bold text-[#1E293B] p-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition flex justify-between items-center"
                >
                  {year}
                  <span className="text-gray-600">{selectedYear === year ? "▲" : "▼"}</span>
                </button>

                {selectedYear === year && (
                  <div className="mt-2 pl-4 border-l-4 border-blue-500">
                    {Object.keys(courses[selectedCourse].pastpapers[year]).map((session) => (
                      <div key={session} className="mb-4">
                        <h5 className="text-md font-semibold text-blue-600 mb-2">{session} Session</h5>
                        {Object.keys(courses[selectedCourse].pastpapers[year][session]).map((folder) => (
                          <div key={folder} className="ml-4 mb-4">
                            <h6 className="text-sm font-medium text-gray-700 mb-2">
                              {folder === "QP" ? "Question Papers" : "Mark Schemes"}
                            </h6>
                            <ul className="space-y-2">
                              {Object.entries(courses[selectedCourse].pastpapers[year][session][folder]).map(
                                ([paperKey, paper]) => (
                                  <li key={paperKey} className="p-3 bg-gray-100 rounded-lg flex justify-between">
                                    <a
                                      href={paper.file}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                    >
                                      {paper.name}
                                    </a>
                                    <button
                                      onClick={() =>
                                        handleDeletePaper(selectedCourse, year, session, folder, paperKey)
                                      }
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      ✖
                                    </button>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default AdminPastPapers;